import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'
import { useAuth } from '../../services/AuthProvider/AuthProvider'
import useApi from '../../api/apiProvider'
import { activateAccountApi, getCurrentAccountApi } from '../../api/account.api'
import { EMBEDDED_LOGIN_URL, LOGIN_CALLBACK_URL } from '../../config/config'
import { useSnackbar } from 'notistack'
import syncPayment from './syncPayment'
import syncAccountName from './syncAccountName'
import syncAddress from './syncAddress'
import syncOrder from './syncOrder'

import { logger } from '../../services/logger'
import { countryManager } from '../../services/CountryManager'
import { deserializeAccountPhotoBook } from '../../services/PhotoBook/photoBookDTO'
import { getOrderDraftSerializedPhotoBooks } from '../../services/PhotoBook/orderDraftPhotoBook'
import StorageManager from '../../services/StorageManager'
import { ACTIVE_ORDERS_COUNT_KEY } from '../../services/LoginUtills'
import {
  useAddressStore,
  usePhotoBankStore,
  usePhotoBooksStore,
  useTilesStore,
} from '../../stores/RootStoreProvider'

function logInitAccountError(error) {
  logger.error('Error initializing account', error)
}

export const CurrentAccountContext = createContext()

CurrentAccountContext.displayName = 'CurrentAccountContext'

export function useAccount() {
  const context = useContext(CurrentAccountContext)

  if (context === undefined) {
    throw new Error('useAccount must be used within a AccountProvider')
  }

  return context
}

export function AccountProvider({ children }) {
  const addressStore = useAddressStore()
  const photoBankStore = usePhotoBankStore()
  const photoBooksStore = usePhotoBooksStore()
  const tilesStore = useTilesStore()
  const { isAuthenticated, isLoading: authLoading, logout } = useAuth()

  const isBookInitialized = useRef(false)

  const api = useApi()
  const [isLoading, setIsLoading] = useState(true)
  const [account, setAccount] = useState()
  const { enqueueSnackbar } = useSnackbar()

  useEffect(() => {
    if (authLoading) return

    // Sign-out / session loss: stop syncing the stores to the now-absent account.
    // Re-enabled by initAccount/syncOrder once an account loads again.
    if (!isAuthenticated) {
      photoBankStore.onSignedOut()
      photoBooksStore.onSignedOut()
      tilesStore.onSignedOut()
      isBookInitialized.current = false
    }

    // If we are in the login callback url we will skip the account because we might be in the middle
    // of account creation and getCurrentAccount will return account not found
    if (
      window.location.pathname === LOGIN_CALLBACK_URL ||
      window.location.pathname === EMBEDDED_LOGIN_URL
    ) {
      setIsLoading(false)
      return
    }

    if (!account) {
      initAccount()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, authLoading, account, photoBankStore, tilesStore])

  const wait = useCallback(async (msec) => {
    await new Promise((resolve) => setTimeout(resolve, msec))
  }, [])
  const initAccount = useCallback(async () => {
    try {
      if (isAuthenticated) {
        let currentAccount
        // there is a sync issue between AuthProvider and AccountProvider:
        // sometimes a user is created and we reach here before loginSuccess is called,
        // so the account is not yet created on the backend. Retry with backoff to absorb the race.
        const retryDelaysMs = [500, 1000, 1500]
        let lastError
        for (const delayMs of retryDelaysMs) {
          await wait(delayMs)
          try {
            currentAccount = await api.call(
              getCurrentAccountApi(countryManager.getCountry())
            )
            lastError = null
            break
          } catch (error) {
            lastError = error
          }
        }
        if (lastError) {
          logInitAccountError(lastError)
          logout()
          return
        }

        if (currentAccount) {
          if (currentAccount.isVerified && !currentAccount.migrationDone) {
            const activatedAccount = await api.call(activateAccountApi())
            setAccount(activatedAccount)
          } else {
            setAccount(currentAccount)
          }

          if (currentAccount.activeOrdersCount !== undefined) {
            StorageManager.set(
              ACTIVE_ORDERS_COUNT_KEY,
              currentAccount.activeOrdersCount
            )
          }

          // We override remote account data with local if local exists and different
          // we are not waiting since we don't want to wait before showing something it can be loaded async
          syncPayment(api, currentAccount.paymentConfig, 'Account Sync')
          syncAddress(api, currentAccount, addressStore)
          // Must run before syncOrder: restored tiles resolve their photos against the bank.
          photoBankStore.onAccountSyncReady(
            currentAccount.photoBank?.photos ?? []
          )
          syncOrder(api, currentAccount, enqueueSnackbar)
          if (!isBookInitialized.current) {
            const { orderDraft } = currentAccount
            const accountBooks = orderDraft
              ? getOrderDraftSerializedPhotoBooks(orderDraft)
                  .map((serializedPhotoBook) =>
                    deserializeAccountPhotoBook({
                      photoBook: serializedPhotoBook,
                      accountPhotos: currentAccount.photoBank?.photos,
                      quantity: serializedPhotoBook.quantity,
                    })
                  )
                  .filter(Boolean)
              : []
            photoBooksStore.onAccountBooksLoaded(
              accountBooks,
              orderDraft?.deletedBookIds ?? []
            )
            isBookInitialized.current = true
          }
          syncAccountName(currentAccount)
          return currentAccount
        }
      } else {
        addressStore.removeAddressIfNotSupported()
      }
    } catch (error) {
      logInitAccountError(error)
    } finally {
      setIsLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, authLoading, account, api.call])

  const value = {
    isLoading,
    isAuthenticated,
    account,
    reloadAccount: initAccount,
  }

  return (
    <CurrentAccountContext.Provider value={value}>
      {children}
    </CurrentAccountContext.Provider>
  )
}
